import { inject, Service } from '@angular/core'
import { Dialog } from '@angular/cdk/dialog'
import { firstValueFrom } from 'rxjs'

import type { MessageData, MessageKind } from './message-modal'

/**
 * Avisos y confirmaciones de la app, con el modal del kit.
 *
 * El componente se carga bajo demanda: la mayoría de las visitas nunca ven un
 * aviso, así que no tiene por qué viajar en el arranque.
 */
@Service()
export class MessagesService {
  readonly #dialog = inject(Dialog)

  async success(title: string, text?: string): Promise<void> {
    await this.#open({ kind: 'success', title, text, confirmText: 'Cerrar' })
  }

  async info(title: string, text?: string): Promise<void> {
    await this.#open({ kind: 'info', title, text, confirmText: 'Cerrar' })
  }

  async error(error: unknown, title = 'Algo salió mal'): Promise<void> {
    await this.#open({ kind: 'error', title, text: this.#toText(error), confirmText: 'Cerrar' })
  }

  /** @returns `true` solo si el participante confirmó. */
  async confirm(title: string, text?: string, confirmButtonText = 'Confirmar'): Promise<boolean> {
    return this.#open({ kind: 'question', title, text, confirmText: confirmButtonText, cancelText: 'Cancelar' })
  }

  async #open(data: MessageData): Promise<boolean> {
    const { MessageModal } = await import('./message-modal')
    const ref = this.#dialog.open<boolean>(MessageModal, { data, panelClass: 'modal-frame' })

    return (await firstValueFrom(ref.closed)) ?? false
  }

  #toText(error: unknown): string {
    if (typeof error === 'string' && error.trim()) return error
    if (error instanceof Error && error.message.trim()) return error.message

    const message = (error as { message?: unknown } | null)?.message
    if (typeof message === 'string' && message.trim()) return message

    return 'Inténtalo de nuevo en unos minutos.'
  }
}

export type { MessageData, MessageKind }
